import { Notification } from '@copera/notification';
import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { useConversationEditorExtensions } from '@modules/conversation/hooks/useConversationEditorExtensions';
import { Stack, Typography, styled } from '@mui/material';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChatMessageListItem } from 'src/@types/chat';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { useNotificationContent } from 'src/features/Notifications/shared/useNotificationContent';
import { useWorkspaceChannel, useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useMentionableParticipants } from '../useMentionableParticipants';
import { ChatMediaNotification } from './ChatMediaContentNotification';

const ContentStyled = styled(ConversationMessageContent)(() => ({
  maxWidth: '40ch',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ChatMessageNotification = ({
  message,
  notification,
}: { message: ChatMessageListItem; notification: Notification }) => {
  const { getNotificationContent } = useNotificationContent();
  const { closeSnackbar } = useSnackbar();
  const sender = useWorkspaceUser(message.author);
  const channel = useWorkspaceChannel(message.channel);
  const [hide, setHide] = useState(false);
  const navigate = useNavigate();

  const { mentionableParticipants } = useMentionableParticipants(
    message.channel,
  );

  const isMedia = useMemo(() => {
    if (
      message.contentType === ConversationMessageContentType.IMAGE ||
      message.contentType === ConversationMessageContentType.VIDEO
    )
      return true;
    return false;
  }, [message.contentType]);

  const { editor } = useConversationEditorExtensions({
    conversationId: message.channel,
    mentionableParticipants,
  }); // TODO - CHECK IF MENTIONABLE IS CORRECT

  if (!editor) return null;

  useEffect(() => setHide(true), []);

  const { navigateTo } = getNotificationContent(notification);

  const handleNavigate = () => {
    navigate(navigateTo || '');
    closeSnackbar();
  };

  return (
    <Stack onClick={handleNavigate}>
      <Stack direction="row" alignItems="center" spacing={1} pb={1}>
        <CPAvatar picture={sender?.picture} name={sender?.name} size="xSmall" />

        <Stack direction="column" spacing={0.5}>
          <Typography variant="subtitle2">
            {sender?.name} at #{channel?.name}
          </Typography>
          {isMedia ? (
            <ChatMediaNotification message={message} />
          ) : (
            <ContentStyled message={message} editor={editor} compact isReply />
          )}
        </Stack>
      </Stack>
      <WidthAnimation className={classNames({ hide })} />
    </Stack>
  );
};

const WidthAnimation = styled('div')(({ theme }) => ({
  background: theme.palette.success.main,
  borderRadius: theme.shape.borderRadius,
  transition: 'width 8s ease-in',
  position: 'absolute',
  height: '8px',
  width: '100%',
  bottom: 0,
  left: 0,

  '&.hide': { width: 0 },
}));
